import InsertBoxComponent from "./components/InsertBoxComponent";
import IpRow from "./components/IpRowComponent";
import ExtractIPsButton from "./components/ExtractIPsButton";
import { useRef, useState } from "react";
import FetchBackendAPI from "./services/FetchBackendAPI";
import FetchOXL from "./services/FetchOXL";
import FetchRIPE from "./services/FetchRIPE";
import ResolveDomain from "./services/ResolveDomain";
import ValidateIP from "./services/ValidateIP";
import EvaluatedIpData from "./structs/EvaluatedIpData";


const App = () => {

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [ipInfos, setIpInfos] = useState<EvaluatedIpData[]>([]);
  const [backendError, setBackendError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getIPInformations = async (ip: string): Promise<EvaluatedIpData> => {
    let backendData: Partial<EvaluatedIpData> = {};
    setBackendError(false);

    try {
      backendData = await FetchBackendAPI(ip);
    } catch (error) {
      console.error("Error fetching from backend API:", error);
      setBackendError(true);

    }
    const commonPorts = backendData.commonPorts?.length
      ? backendData.commonPorts
      : [{ port: "n/a", open: false }];

    const [oxlData, ripeData] = await Promise.all([FetchOXL(ip), FetchRIPE(ip)]);

    return {
      ip: backendData.ip || oxlData.ip,
      abuse: backendData.abuse || "n/a",
      abuseMail: ripeData.abuseMail || "n/a",
      ping: backendData.ping || false,
      commonPorts,
      inBlocklist: backendData.inBlocklist ?? "n/a",
      hostname: backendData.hostname || oxlData.hostname,
      country: ripeData.country || oxlData.country || backendData.country || "n/a",
      org: backendData.org || oxlData.org,
      company: backendData.company || oxlData.company,
      asn: oxlData.asn ?? "n/a",
      nat: backendData.nat ?? oxlData.nat,
    };
  };


  const handleClick = async () => {
    const value = textareaRef.current?.value ?? "";
    const ips = await extractIPs(value);
    setIpInfos([]);
    const maxLookups = 35;
    if (ips.length >= maxLookups) {
      alert(`Please enter a maximum of ${maxLookups - 1} IP addresses or hostnames at once.`);
      return;
    }
    setIsLoading(true);
    setIpInfos([]);
    for (const ip of ips) {
      try {
        const data = await getIPInformations(ip);
        setIpInfos(prev => [...prev, data]);
      } catch (err) {
        console.error(`Error with ${ip}:`, err);
      }
    }
    setIsLoading(false);

  };




  const extractIPs = async (text: string): Promise<string[]> => {
    const ipv4Regex = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
    const ipv6Regex = /\b(?:[a-fA-F0-9]{1,4}:){2,7}[a-fA-F0-9]{0,4}\b/g;
    const domainRegex = /\b((?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})\b/g;

    const ipv4s = (text.match(ipv4Regex) || []).filter(ip => {
      const result = ValidateIP(ip);
      return result.valid && result.version === "ipv4";
    });

    const ipv6s = (text.match(ipv6Regex) || []).filter(ip => {
      const result = ValidateIP(ip);
      return result.valid && result.version === "ipv6";
    });

    const domains = (text.match(domainRegex) || []).filter(
      d => d.toLowerCase() !== "localhost"
    );

    const resolvedIPs: string[] = [];

    for (const domain of domains) {
      try {
        const ips = await ResolveDomain(domain);
        if (ips.length > 0) {
          resolvedIPs.push(...ips);
        } else {
          console.warn(`Domain could not be resolved: ${domain}`);
        }
      } catch (err) {
        console.error(`Error resolving domain ${domain}:`, err);
      }
    }

    return Array.from(new Set([...ipv4s, ...ipv6s, ...resolvedIPs]));
  };



  return (
  <>
    <div id="screen-warning">
      <strong>Insufficient Screen Resolution</strong>
      <span>
        This application is optimized for large displays.<br />
        Please expand your browser window to at least 1200px.
      </span>
    </div>

    <div className={`top-loading-bar ${isLoading ? 'active' : ''}`}></div>

    <header>
      <span className="version">v2026-02</span>
      <div className="rightHeader">
        <h1>
          <span className="highlight-box">IP Intelligence</span> Dashboard
        </h1>
      </div>
    </header>

    {backendError && (
      <div className="backend-error-banner">
        <strong>⚠️ Backend could not be reached!</strong>
        <p>
          Some data as abuse score, ping and port check might be missing...
        </p>
      </div>
    )}

    <div className="dashboard">
      <div className="input-card">
        <InsertBoxComponent inputRef={textareaRef} />
        <ExtractIPsButton onClick={handleClick} loading={isLoading} />
      </div>

      <div className="output-card">
        <div className="progress-container">
          {isLoading && <div className="progress-bar-indeterminated"></div>}
        </div>

        {ipInfos.length > 0 ? (
          <ul className="ip-list">
            {ipInfos.map((info, index) => (
              <IpRow key={index} {...info} />
            ))}
          </ul>
        ) : (
          !isLoading && (
            <div className="empty-state">
              
            </div>
          )
        )}
      </div>
    </div>

    <footer className="footer">
      <p>
        🚀 Get Code on&nbsp;
        <a href="https://github.com/Teronisos/ip-intelligence" target="_blank" rel="noreferrer">
          GitHub
        </a>
      </p>
    </footer>
  </>
);
};
export default App;

